import AddIcon from "@mui/icons-material/Add"
import CancelIcon from "@mui/icons-material/Cancel"
import DeleteIcon from "@mui/icons-material/Delete"
import PdfIcon from "@mui/icons-material/PictureAsPdf"
import SaveIcon from "@mui/icons-material/Save"
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  TextField,
} from "@mui/material"
import dayjs from "dayjs"
import "dayjs/locale/bn-bd"
import { getAnalytics, logEvent } from "firebase/analytics"
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  where,
  writeBatch,
} from "firebase/firestore"
import { FunctionComponent, useEffect, useState } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { useHistory, useParams } from "react-router-dom"
import Color from "../Color"
import { Collection, Document, Field } from "../Enum"
import { paperStyleDefault } from "../Style"
import { timber_debug, timber_warn } from "../Timber"
import { Buyer, Inventory, Invoice, InvoiceItem } from "../Type"
import {
  getEmptyInvoice,
  getInvoiceFromDocSnapshot,
} from "../contexts/InvoiceContext"
import { formatCurrencyAmount, getTotalPrice } from "../utils/CurrencyUtils"
import { fixInvoiceDate, formatDateTime } from "../utils/DateUtils"
import { generateInvoicePdfFile } from "../utils/PdfUtils"
import InvoiceItemView from "./InvoiceItemView"

/**
 * @param param
 * @returns
 */
const InvoiceView: FunctionComponent<{
  userId: string
  // Not possible to pass any other props here because of routing from App component
}> = ({ userId }) => {
  const intl = useIntl()
  /**
   * id is undefined in case of new invoice. Populated with firestoreId otherwise
   */
  const { id } = useParams<{ id: string }>()
  const history = useHistory()

  // Firebase
  const firestore = getFirestore()
  const analytics = getAnalytics()
  const invoicesRef = collection(
    firestore,
    `/${Collection.Users}/${userId}/${Collection.Invoices}`
  )
  const inventoriesRef = collection(
    firestore,
    `/${Collection.Users}/${userId}/${Collection.Inventories}`
  )
  const invoicesItemsRef = collection(
    firestore,
    `/${Collection.Users}/${userId}/${Collection.InvoiceItems}`
  )
  const buyersRef = collection(
    firestore,
    `/${Collection.Users}/${userId}/${Collection.Buyers}`
  )
  const buyersQuery = query(buyersRef, orderBy(`${Field.Name}`))
  const profileRef = doc(
    firestore,
    `/${Collection.Users}/${userId}/${Collection.Private}`,
    Document.Profile
  )

  // useState
  const [invoice, setInvoice] = useState<Invoice>(getEmptyInvoice())
  const [invoiceDate, setInvoiceDate] = useState<string>(formatDateTime())
  const [inventories, setInventories] = useState<Inventory[]>([])
  const [totalPrice, setTotalPrice] = useState<number>(0)
  const [buyers, setBuyers] = useState<Buyer[]>([])
  const [buyer, setBuyer] = useState<Buyer>()
  const [invoiceItems, setInvoiceItems] = useState<InvoiceItem[]>([])

  // Business profile
  let name = ""
  let addressLineOne = ""
  let addressLineTwo = ""
  let phone = ""
  let email = ""
  let website = ""

  // Buyer profile
  let buyerName = ""
  let buyerAddressLineOne = ""
  let buyerAddressLineTwo = ""
  let buyerPhone = ""
  let buyerEmail = ""
  let buyerWebsite = ""

  /**
   * Gets inventories
   */
  useEffect(() => {
    // Sets up real-time Inventories listener
    timber_debug("useEffect subscribeInventoriesListener")
    const unsubscribeInventoriesListener = onSnapshot(
      inventoriesRef,
      (querySnapshot) => {
        const newInventories: Inventory[] = []
        querySnapshot.forEach((doc) => {
          newInventories.push({
            id: doc.id,
            name: doc.data().name,
            quantity: doc.data().quantity,
            purchasePrice: doc.data().purchasePrice,
            unitPrice: doc.data().unitPrice,
            inventoryImageName: doc.data().inventoryImageName,
            inventoryImageUri: doc.data().inventoryImageUri,
            insertDate: doc.data().insertDate,
            lastEditDate: doc.data().lastEditDate,
          })
        })
        timber_debug(`Downloaded ${newInventories.length} inventories`)
        setInventories(newInventories)
      },
      (error) => {
        console.error("Error fetching inventories", error)
      }
    )
    return () => {
      timber_debug("useEffect unsubscribeInventoriesListener")
      unsubscribeInventoriesListener()
    }
  }, [])

  /**
   * Gets buyers and invoice in the same useEffect to prevent race condition
   */
  useEffect(() => {
    // Sets up real-time buyers listener
    timber_debug("useEffect subscribeBuyersListener")
    const unsubscribeBuyersListener = onSnapshot(
      buyersQuery,
      (querySnapshot) => {
        const newBuyers: Buyer[] = []
        querySnapshot.forEach((doc) => {
          newBuyers.push({
            id: doc.id,
            name: doc.data().name,
            address: doc.data().address,
            phone: doc.data().phone,
            email: doc.data().email,
            website: doc.data().website,
            insertDate: dayjs().valueOf(),
            lastEditDate: dayjs().valueOf(),
          })
        })
        timber_debug(`Downloaded ${newBuyers.length} buyers`)
        setBuyers(newBuyers)

        // Download invoice now to prevent race condition
        timber_debug(`invoiceId: ${id}`)
        if (id === undefined) {
          // New invoice
          setInvoice(getEmptyInvoice())
          setInvoiceDate(formatDateTime())
        } else {
          // Existing invoice
          getDoc(
            doc(
              firestore,
              `/${Collection.Users}/${userId}/${Collection.Invoices}`,
              id
            )
          ).then((docSnap) => {
            if (docSnap.exists()) {
              timber_debug("Downloaded invoice snapshot")
              setInvoice(getInvoiceFromDocSnapshot(docSnap))
              setInvoiceDate(fixInvoiceDate(docSnap.data().date))
              // Don't know why buyers state is empty at this stage. Is it slow?
              // To fix this, we filter directly from newBuyers array
              setBuyer(
                newBuyers.filter(
                  (item) => item.id === docSnap.data().buyerId
                )[0]
              )
            } else {
              setInvoice(getEmptyInvoice())
              setInvoiceDate(formatDateTime())
              timber_warn(`Existing invoice not present in firestore id: ${id}`)
            }
          })
        }
      },
      (error) => {
        console.error("Error fetching buyers", error)
      }
    )
    return () => {
      timber_debug("useEffect unsubscribeBuyersListener")
      unsubscribeBuyersListener()
    }
  }, [])

  /**
   * Gets invoice items from firestore for existing invoice.
   * For new invoice, adds new invoice item
   */
  useEffect(() => {
    if (id !== undefined) {
      getDocs(query(invoicesItemsRef, where("invoiceId", "==", id)))
        .then((querySnapshot) => {
          const newInvoiceItems: InvoiceItem[] = []
          querySnapshot.forEach((doc) => {
            newInvoiceItems.push({
              id: doc.id,
              index: Math.floor(Math.random() * 10000) + 1, // to keep track of children in list
              invoiceId: doc.data().invoiceId,
              name: doc.data().name,
              quantity: doc.data().quantity,
              unitPrice: doc.data().unitPrice,
              totalPrice: doc.data().totalPrice,
              insertDate: doc.data().insertDate,
              lastEditDate: doc.data().lastEditDate,
            })
          })
          timber_debug(`Downloaded ${newInvoiceItems.length} invoiceItems`)
          setInvoiceItems(newInvoiceItems)
          setTotalPrice(formatCurrencyAmount(getTotalPrice(invoiceItems)))
        })
        .catch((error) => {
          console.error(error)
          addInvoiceItem()
        })
    } else {
      addInvoiceItem()
    }
    return () => {}
  }, [])

  const saveInvoice = () => {
    logEvent(analytics, "web_create_invoice_clicked")
    timber_debug(`Total invoice items: ${invoiceItems.length}`)
    const currentTime = dayjs().valueOf()
    if (id === undefined) {
      // Saves a new invoice
      const invoiceRef = doc(invoicesRef)
      setDoc(invoiceRef, {
        date: invoiceDate,
        name: buyer?.name,
        buyerId: buyer?.id,
        totalItems: getTotalQuantity(invoiceItems),
        totalPrice: getTotalPrice(invoiceItems),
        insertDate: currentTime,
        lastEditDate: currentTime,
      })
        .then(() => {
          timber_debug("Invoice created successfully")
          logEvent(analytics, "web_invoice_created")

          // Saves invoice items
          const promises = []
          for (let item of invoiceItems) {
            promises.push(
              addDoc(invoicesItemsRef, {
                name: item.name ? item.name : "",
                quantity: item.quantity ? item.quantity : 0,
                unitPrice: item.unitPrice ? item.unitPrice : 0,
                totalPrice: item.totalPrice ? item.totalPrice : 0,
                invoiceId: invoiceRef.id,
                insertDate: currentTime,
                lastEditDate: currentTime,
              })
            )
          }
          return Promise.all(promises)
        })
        .then(() => {
          timber_debug("Invoice items created successfully")
          history.push(`/invoice-list`)
        })
        .catch((error) => {
          console.error(error)
          history.push(`/invoice-list`)
        })
    } else {
      // Saves an edited invoice
      logEvent(analytics, "web_edit_invoice_clicked")
      const invoiceRef = doc(firestore, invoicesRef.path, id)
      setDoc(invoiceRef, {
        date: invoiceDate,
        name: buyer?.name,
        buyerId: buyer?.id,
        totalItems: getTotalQuantity(invoiceItems),
        totalPrice: getTotalPrice(invoiceItems),
        insertDate: invoice.insertDate,
        lastEditDate: currentTime,
      })
        .then(() => {
          timber_debug("Invoice edited successfully")
          // Find old invoice items
          return getDocs(query(invoicesItemsRef, where("invoiceId", "==", id)))
        })
        .then((querySnapshot) => {
          // Deletes old invoice items
          const batch = writeBatch(firestore)
          querySnapshot.forEach((doc) => {
            batch.delete(doc.ref)
          })
          timber_debug(
            `${querySnapshot.size} invoiceItems are selected for deletion`
          )
          return batch.commit()
        })
        .then(() => {
          timber_debug("Old Invoice items deleted successfully")
          // Saves invoice items
          const promises = []
          for (let item of invoiceItems) {
            promises.push(
              addDoc(invoicesItemsRef, {
                name: item.name,
                quantity: item.quantity,
                unitPrice: item.unitPrice,
                totalPrice: item.totalPrice,
                invoiceId: invoiceRef.id,
                insertDate: currentTime,
                lastEditDate: currentTime,
              })
            )
          }
          return Promise.all(promises)
        })
        .then(() => {
          timber_debug("New Invoice items saved successfully")
          logEvent(analytics, "web_invoice_edited")
          history.push(`/invoice-list`)
        })
        .catch((error: Error) => {
          console.error(error)
          logEvent(analytics, "web_save_invoice_error")
          history.push(`/invoice-list`)
        })
    }
  }

  const deleteInvoice = () => {
    logEvent(analytics, "web_invoice_deleted")

    if (id !== undefined && id !== "") {
      // First, deletes invoice items
      getDocs(query(invoicesItemsRef, where("invoiceId", "==", id)))
        .then((querySnapshot) => {
          const batch = writeBatch(firestore)
          querySnapshot.forEach((doc) => {
            batch.delete(doc.ref)
          })
          timber_debug(
            `${querySnapshot.size} invoiceItems are selected for deletion`
          )
          return batch.commit()
        })
        .then(() => {
          timber_debug(`InvoiceItems for invoice id ${id} deleted successfully`)
          // Finally, delete invoice
          return deleteDoc(doc(firestore, invoicesRef.path, id))
        })
        .then(() => {
          timber_debug(`Invoice with id ${id} deleted successfully`)
          logEvent(analytics, "web_invoice_deleted")
          history.push(`/invoice-list`)
        })
        .catch((error: Error) => {
          console.log(error)
          logEvent(analytics, "web_delete_invoice_error")
        })
    } else {
      timber_warn("Unable to delete invoice: empty invoiceId")
    }
  }

  const addInvoiceItem = () => {
    setInvoiceItems([
      ...invoiceItems,
      {
        id: "",
        index: Math.floor(Math.random() * 10000) + 1,
        invoiceId: invoice.id,
        name: "",
        quantity: 1,
        unitPrice: 0,
        totalPrice: 0,
        insertDate: dayjs().valueOf(),
        lastEditDate: dayjs().valueOf(),
      },
    ])
    setTotalPrice(formatCurrencyAmount(getTotalPrice(invoiceItems)))
  }

  const removeInvoiceItem = (invoiceItem: InvoiceItem) => {
    timber_debug("removeInvoiceItem")
    setInvoiceItems(
      invoiceItems.filter((item) => item.index !== invoiceItem.index)
    )
    setTotalPrice(formatCurrencyAmount(getTotalPrice(invoiceItems)))
  }

  const getTotalQuantity = (invoiceItems: InvoiceItem[]) => {
    let totalQuantity = 0
    for (let item of invoiceItems) {
      totalQuantity += item.quantity
    }
    return totalQuantity
  }

  const updateTotalPrice = () => {
    timber_debug("updateTotalPrice")
    setTotalPrice(formatCurrencyAmount(getTotalPrice(invoiceItems)))
  }

  const exportAsPdf = () => {
    getDoc(profileRef)
      .then((docSnap) => {
        if (docSnap.exists()) {
          timber_debug("Downloaded profile snapshot")
          name = docSnap.data().name
          addressLineOne = docSnap.data().addressLineOne
            ? docSnap.data().addressLineOne
            : ""
          addressLineTwo = docSnap.data().addressLineTwo
            ? docSnap.data().addressLineTwo
            : ""
          phone = docSnap.data().phone
          email = docSnap.data().email
          website = docSnap.data().website
        } else {
          timber_debug("No profile doc exists")
        }
      })
      .then(() => {
        return getDoc(
          doc(firestore, `/users/${userId}/buyers`, invoice.buyerId)
        )
      })
      .then((docSnap) => {
        if (docSnap.exists()) {
          timber_debug("Downloaded buyer profile snapshot")
          buyerName = docSnap.data().name
          buyerAddressLineOne = docSnap.data().addressLineOne
            ? docSnap.data().addressLineOne
            : ""
          buyerAddressLineTwo = docSnap.data().addressLineTwo
            ? docSnap.data().addressLineTwo
            : ""
          buyerPhone = docSnap.data().phone
          buyerEmail = docSnap.data().email
          buyerWebsite = docSnap.data().website
        } else {
          timber_debug("No buyer profile doc exists")
        }
      })
      .then(() => {
        generateInvoicePdfFile(
          invoiceItems,
          name,
          addressLineOne,
          addressLineTwo,
          phone,
          email,
          buyerName,
          buyerPhone,
          buyerEmail,
          buyerAddressLineOne,
          buyerAddressLineTwo,
          analytics
        )
      })
      .catch((error) => {
        console.error(error)
      })
  }

  return (
    <Box height="91vh" display="flex" flexDirection="column">
      <Paper
        elevation={3}
        style={{
          height: "85vh",
          margin: "8px",
          ...paperStyleDefault,
          padding: "8px",
        }}
      >
        <DialogTitle>
          {id === undefined ? (
            <FormattedMessage id="dialog.title.create.invoice" />
          ) : (
            <FormattedMessage id="dialog.title.edit.invoice" />
          )}
        </DialogTitle>
        <DialogContent>
          <Grid
            container
            spacing={1}
            direction={"row"}
            alignItems="center"
            rowSpacing={5}
          >
            {/* Invoice date */}
            {/* Does NOT work because dayjs cannot convert bengali date string */}
            {/* <Grid size={{ sm: 12, md: 12, lg: 12 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label={intl.formatMessage({
                    id: "table.headers.date",
                  })}
                  value={getDayJSObjectFromString(invoiceDate)}
                  onChange={(newDate) => {
                    logEvent(analytics, "web_invoice_date_selected")
                    setInvoiceDate(formatDateTime(dayjs(newDate).valueOf()))
                  }}
                  format={DATE_FORMAT}
                />
              </LocalizationProvider>
            </Grid> */}

            {/* Buyer name list */}
            <Grid size={{ sm: 12, md: 12, lg: 12 }}>
              <TextField
                fullWidth
                select
                label={intl.formatMessage({
                  id: "invoice.buyer",
                })}
                value={buyer ? buyer.name : ""}
                onChange={(event: any) => {
                  logEvent(analytics, "web_invoice_buyer_selected")
                  setBuyer(
                    buyers.filter(
                      (buyer) => buyer.name === event.target.value
                    )[0]
                  )
                }}
              >
                {buyers.map((buyer, index) => (
                  <MenuItem key={index} value={buyer.name}>
                    {buyer.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          {/* Invoice Item list */}
          {invoiceItems.map((invoiceItem) => (
            <Grid marginTop={2} key={invoiceItem.index}>
              <InvoiceItemView
                invoiceItem={invoiceItem}
                inventories={inventories}
                onRemoveInvoiceItem={removeInvoiceItem}
                onUpdateTotalPrice={updateTotalPrice}
              ></InvoiceItemView>
            </Grid>
          ))}

          <Grid container>
            <Grid size={{ sm: 8, md: 8, lg: 8 }}>
              {/* Add invoice item */}
              <Button
                onClick={addInvoiceItem}
                variant="contained"
                startIcon={<AddIcon />}
                sx={{ marginTop: 1 }}
              >
                <FormattedMessage id="dialog.button.add.item" />
              </Button>
            </Grid>
            <Grid marginTop={2} size={{ sm: 3, md: 3, lg: 3 }}>
              <TextField
                disabled
                fullWidth
                type="number"
                label={intl.formatMessage({
                  id: "invoice.total.price",
                })}
                value={formatCurrencyAmount(getTotalPrice(invoiceItems))}
              ></TextField>
            </Grid>
          </Grid>
        </DialogContent>

        {/* Dialog Action Buttons */}
        <DialogActions>
          <Button
            onClick={() => {
              history.push(`/invoice-list`)
            }}
            variant="outlined"
            startIcon={<CancelIcon />}
          >
            <FormattedMessage id="dialog.button.cancel" />
          </Button>
          {id !== undefined && (
            <Button
              color="error"
              onClick={deleteInvoice}
              variant="outlined"
              startIcon={<DeleteIcon />}
            >
              <FormattedMessage id="dialog.button.delete" />
            </Button>
          )}
          {id !== undefined && (
            <Button
              onClick={() => exportAsPdf()}
              variant="outlined"
              style={{ color: Color.Black }}
              startIcon={<PdfIcon />}
            >
              <FormattedMessage id="button.pdf" />
            </Button>
          )}
          <Button
            onClick={saveInvoice}
            variant="contained"
            startIcon={<SaveIcon />}
          >
            <FormattedMessage id="dialog.button.save" />
          </Button>
        </DialogActions>
      </Paper>
    </Box>
  )
}

export default InvoiceView
